var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { capitalize } from "lodash";
import * as React from "react";
import MuiAlert from "@material-ui/lab/Alert";
import { getThemeProps } from "@material-ui/styles";
import { AlertTitle } from "../alert-title";
import { AlertCircleIcon, AlertTriangleIcon, CheckCircleIcon, FileTextIcon, InfoIcon, XIcon } from "../icon";
import { IconButton } from "../icon-button";
import { makeStyles, useTheme } from "../styles";
import clsx from "../utils/clsx";
import { getCosmosDataProps } from "../utils/comsosCompat";
var AlertComponentName = "CosmosAlert";
export var useAlertStyles = makeStyles(function (theme) { return ({
    standardDefault: {
        borderLeft: "8px solid " + theme.mixins.shadeOfColor("neutral", "base"),
        color: theme.mixins.textColorForShadeOfColor("neutral", "lightest"),
        backgroundColor: theme.mixins.shadeOfColor("neutral", "lightest"),
        "& $icon": {
            color: theme.mixins.textColorForShadeOfColor("neutral", "lightest"),
        },
    },
    outlined: {
        "& $closeButton": {
            borderRadius: "50%",
            padding: theme.spacing(0.5),
            height: theme.typography.pxToRem(24),
            width: theme.typography.pxToRem(24),
        },
        "& a": {
            color: theme.palette.primary.main,
            textDecoration: "none",
            "&:hover": {
                color: theme.palette.primary[800] || theme.palette.primary.dark,
                textDecoration: "none",
            },
        },
    },
    outlinedDefault: {
        border: "1px solid " + theme.palette.border.dark,
        borderLeft: "8px solid " + theme.mixins.shadeOfColor("neutral", "base"),
        color: theme.palette.text.primary,
        "& $icon": {
            color: theme.mixins.shadeOfColor("neutral", "dark"),
        },
    },
    icon: {
        fontSize: theme.typography.pxToRem(20),
        "& svg": {
            height: "1em",
            width: "1em",
        },
    },
    closeButton: {
        borderRadius: "50%",
        padding: theme.spacing(0.5),
        height: theme.typography.pxToRem(24),
        width: theme.typography.pxToRem(24),
    },
}); }, { name: AlertComponentName });
export var getSeverityForAppearance = function (appearance) {
    switch (appearance) {
        case "information":
            return "info";
        case "danger":
            return "error";
        default:
            return appearance;
    }
};
var defaultIconMapping = {
    warning: React.createElement(AlertTriangleIcon, null),
    error: React.createElement(AlertCircleIcon, null),
    success: React.createElement(CheckCircleIcon, null),
    info: React.createElement(InfoIcon, null),
    default: React.createElement(FileTextIcon, null),
};
export var Alert = React.forwardRef(function (props, ref) {
    var theme = useTheme();
    var classes = useAlertStyles(props);
    var _a = getThemeProps({ theme: theme, props: __assign({}, props), name: "MuiAlert" }), severityProp = _a.severity, iconProp = _a.icon, onCloseProp = _a.onClose, appearance = _a.appearance, _b = _a.dismissible, dismissible = _b === void 0 ? true : _b, _c = _a.variant, variant = _c === void 0 ? "standard" : _c, onDismiss = _a.onDismiss, title = _a.title, action = _a.action, children = _a.children, _d = _a.iconMapping, iconMapping = _d === void 0 ? defaultIconMapping : _d, className = _a.className, CloseButtonProps = _a.CloseButtonProps, 
    // remove to prevent passing through to the root
    propClasses = _a.classes, rootProps = __rest(_a, ["severity", "icon", "onClose", "appearance", "dismissible", "variant", "onDismiss", "title", "action", "children", "iconMapping", "className", "CloseButtonProps", "classes"]);
    var severity = severityProp || getSeverityForAppearance(appearance) || "default";
    var onClose = onCloseProp || onDismiss;
    var icon = iconProp != null ? iconProp : iconMapping[severity];
    return (React.createElement(MuiAlert, __assign({ icon: icon, severity: severity === "default" ? undefined : severity, className: clsx(variant && classes[variant], classes["" + variant + capitalize(severity)], className), classes: { icon: classes.icon }, variant: variant, ref: ref, action: action ||
            (dismissible && onClose && (React.createElement(IconButton, __assign({ variant: variant === "outlined" ? "contained" : "text", className: classes.closeButton, size: "small", color: "inherit", onClick: onClose }, CloseButtonProps),
                React.createElement(XIcon, null)))) }, getCosmosDataProps("alert"), rootProps),
        !!title && React.createElement(AlertTitle, null, title),
        children));
});
